import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime } from 'rxjs';
import {
  BusinessType,
  ClientDto,
  DateTypes,
  InvoiceType,
} from 'src/app/api/invoice/models';
import { ClientService } from 'src/app/api/invoice/services';
import { IFilterCompProp, IListParams } from '../../models/user.model';
import { CustomValidators } from '../../customValidators/validator';
import { RedirectService } from '../../services/redirect-service';

@Component({
  selector: 'app-filter-component',
  templateUrl: './filter-component.html',
  styleUrls: ['./filter-component.scss'],
})
export class FilterComponent {
  @Input() childProp: IFilterCompProp = { buttonText: '', title: '' };
  @Output() filterSubmit: EventEmitter<any> = new EventEmitter();
  @Input() loaderVariable = false;

  clientParams = { page: 0, size: 100, search: '' };
  private inputSubject = new Subject<string>();
  params = { page: 0, size: 300, search: '' };
  clientOptions: ClientDto[] = [];
  isFieldFocused = false;
  businessTypes = Object.values(BusinessType);
  invoiceTypes = Object.values(InvoiceType);
  reportLoading = false;
  clientLoading = false;
  maxDate = new Date();

  dateOptions = [
    { name: 'Invoice Date', value: DateTypes.Invoicedate },
    { name: 'Service Date', value: DateTypes.Servicedate },
  ];

  filterForm!: UntypedFormGroup;
  businessTypesOptions: BusinessType[] = [];
  allowedBusinessTypes: string[] = [];

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private toastr: ToastrService,
    private redirectService: RedirectService
  ) {
    this.filterForm = this.createCreditNoteForm();
    this.filterForm.controls?.['fromDate'].valueChanges.subscribe(() => {
      this.setMaxDate();
    });
  }

  createCreditNoteForm() {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 3); // Subtract 3 months

    const toDate = new Date();

    return this.fb.group(
      {
        dateType: new UntypedFormControl({
          name: 'Invoice Date',
          value: DateTypes.Invoicedate,
        }),
        fromDate: new UntypedFormControl(fromDate, [Validators.required]),
        toDate: new UntypedFormControl(toDate, [Validators.required]),
        clientId: new UntypedFormControl(),
        invoiceType: new UntypedFormControl(),
        businessType: new UntypedFormControl(),
      },
      {
        validator: CustomValidators.maximumThreeMonthsRangeValidator(
          'fromDate',
          'toDate'
        ),
      }
    );
  }
  setMaxDate() {
    const fromDate = this.filterForm.controls?.['fromDate'].value;
    const maxDate = new Date(fromDate);
    maxDate.setMonth(maxDate.getMonth() + 3); // Add 3 months
    this.maxDate = maxDate;
  }

  clearSearchClients() {
    this.clientParams.search = '';
    this.fetchClients(this.params);
  }
  onInput(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.inputSubject.next(searchTerm);
  }

  fetchClients(params: IListParams) {
    this.clientLoading = true;
    this.clientService.getAllClients(params).subscribe({
      next: response => {
        if (response?.data) {
          this.clientOptions = response.data[0]?.data || [];
        }
        this.clientLoading = false;
      },
      error: error => {
        this.clientLoading = false;

        this.toastr.error(error?.error?.message);
      },
    });
  }
  customSearchFn(term: string, item: ClientDto) {
    return item;
  }

  ngOnInit() {
    this.fetchClients(this.clientParams);
    // fetch data on search
    this.inputSubject
      //  debounce time as needed
      .pipe(debounceTime(1000))
      .subscribe(searchTerm => {
        this.clientParams.search = searchTerm;
        this.fetchClients(this.clientParams);
      });
    this.filterBusinessOptions();
  }
  filterBusinessOptions() {
    const permissionMap: { [key: string]: BusinessType } = {
      SALES_MODULE: BusinessType.Sales,
      FINANCE_MODULE: BusinessType.Finance,
      MEDVAC_MODULE: BusinessType.Medvac,
      MCC_MODULE: BusinessType.Mcc,
      GH_MODULE: BusinessType.Gh,
      ESCORT_MODULE: BusinessType.Escort,
      TRAINING_MODULE: BusinessType.Training,
      SALES_ORDER_MODULE: BusinessType.SalesOrder,
      ACM_MODULE: BusinessType.Acm,
      FLIGHT_MODULE: BusinessType.Flight,
      SALES_WORK_ORDER_MODULE: BusinessType.SalesWorkOrder,
      AIRPORT_MANAGEMENT_SERVICE_MODULE: BusinessType.AirportManagementService,
      ACMS_SALES_MODULE: BusinessType.AcmsSales,
      ACMS_SALES_WORK_ORDER_MODULE: BusinessType.AcmsSalesWorkOrder,
    };

    const businessOptions: BusinessType[] = Object.keys(permissionMap).reduce<
      BusinessType[]
    >((options, permission) => {
      if (this.redirectService.hasPermission(permission)) {
        options.push(permissionMap[permission as keyof typeof permissionMap]);
      }
      return options;
    }, []);
    this.allowedBusinessTypes = [...businessOptions];

    // Check if all three permissions are granted
    const allPermissionsGranted = Object.keys(permissionMap).every(permission =>
      this.redirectService.hasPermission(permission)
    );

    // if all three permissions
    if (allPermissionsGranted) {
      businessOptions.push('ALL' as any);
    }

    this.businessTypesOptions = businessOptions;
  }
  onClear() {
    this.filterForm.reset(this.createCreditNoteForm().value);
  }

  onBusinessTypeChange(selectedValues: string[]) {
    const allIndex = selectedValues.indexOf('ALL');
    if (allIndex !== -1) {
      this.filterForm.patchValue({
        businessType: ['ALL'],
      });
    }
  }

  onFocus(isFocused: boolean, name: string) {
    this.isFieldFocused = isFocused;

    switch (name) {
      case 'clientId':
        if (!isFocused) {
          this.inputSubject.next('');
        }
        break;

      default:
        break;
    }
  }
  onSubmit() {
    const filterData = {
      filterForm: this.filterForm,
      allowedBusinessTypes: this.allowedBusinessTypes,
    };

    this.filterSubmit.emit(filterData);
  }
}
